import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { ScrollIcon, LanternIcon, ChevronRight, PennantIcon } from '@/components/game/GameIcons'
import { SeaScene } from '@/components/game/SeaScene'
import { SeoHead } from '@/components/SeoHead'
import { ROUTE_SEO } from '@/seo'
import { useNews, type NewsBlock, type NewsPost } from '@/api/hooks'

/**
 * /nouveautes — « Le journal de bord » : les annonces du capitaine de port
 * (bilans de saison, gros lots livrés, ce qui arrive).
 *
 * Page PUBLIQUE (hors ProtectedRoute), au même titre que /codex et /legal : un
 * visiteur doit pouvoir lire les nouvelles avant de s'inscrire.
 *
 * ⚓ Chaque billet est rendu dans un <article id={slug}> : la notification de
 * broadcast pointe sur /nouveautes#<slug>, l'ancre DOIT rester en place.
 *
 * 🔒 Le contenu arrive en blocs de TEXTE (union discriminée `NewsBlock`), jamais
 * en HTML — donc jamais de `dangerouslySetInnerHTML` ici. C'est le choix de
 * design du contrat : zéro surface XSS.
 */

const MONTHS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
]

/** "2026-07-28" → "28 juillet 2026". Rend la chaîne brute si le format surprend. */
function frenchDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return iso
  const month = MONTHS[Number(m[2]) - 1]
  if (!month) return iso
  return `${Number(m[3])} ${month} ${m[1]}`
}

export function Nouveautes() {
  const { data, isLoading, isError } = useNews()
  const { hash } = useLocation()
  const posts = data?.posts ?? []

  // Les liens de notification arrivent avec une ancre (#<slug>). React Router ne
  // défile pas tout seul, et la cible n'existe qu'une fois les billets chargés :
  // on refait donc la passe à chaque changement de hash ET à l'arrivée des données.
  useEffect(() => {
    if (!hash) return
    const target = document.getElementById(hash.slice(1))
    target?.scrollIntoView({ block: 'start' })
  }, [hash, posts.length])

  return (
    <div className="relative">
      <SeoHead {...ROUTE_SEO['/nouveautes']} />
      <style>{'@media (prefers-reduced-motion: no-preference){html{scroll-behavior:smooth}}'}</style>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-[320px]">
        <Hero count={posts.length} />

        {isLoading && (
          <p className="mt-10 text-center text-sm text-ocean-300">Chargement du journal de bord…</p>
        )}

        {!isLoading && isError && (
          <p className="mt-10 text-center text-sm text-rum-100">
            Impossible de charger le journal de bord. Reviens dans un moment, capitaine.
          </p>
        )}

        {!isLoading && !isError && posts.length === 0 && <EmptyState />}

        {!isLoading && !isError && posts.length > 1 && <QuickNav posts={posts} />}

        {!isLoading && !isError && posts.length > 0 && (
          <div className="mt-10 space-y-10">
            {posts.map((post) => (
              <Post key={post.slug} post={post} />
            ))}
          </div>
        )}
      </div>

      {/* même toile de fond maritime que le Codex */}
      <SeaScene scene="map" />
    </div>
  )
}

/* ──────────────────────────────── hero ──────────────────────────────── */

function Hero({ count }: { count: number }) {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/45 p-7 sm:p-9">
      <CompassWatermark />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
        <span className="grid place-items-center w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-2xl bg-ocean-900/60 border-2 border-sand-500/30 text-sand-300">
          <ScrollIcon className="w-10 h-10 sm:w-12 sm:h-12" />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.32em] text-ocean-300 mb-1.5">Corsalia · Journal de bord</p>
          <h1 className="font-display text-4xl sm:text-5xl text-sand-300 leading-none">Nouveautés</h1>
          <p className="mt-3 text-ocean-100 leading-relaxed max-w-2xl">
            Les annonces de l'archipel : bilans de saison, nouveautés à bord et ce qui se prépare à l'horizon.
            Du plus récent au plus ancien.
          </p>
          {count > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs px-2.5 py-1 rounded-full border bg-sand-300/15 border-sand-300/40 text-sand-200">
                {count} {count > 1 ? 'annonces' : 'annonce'}
              </span>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

function CompassWatermark() {
  return (
    <div className="pointer-events-none absolute -right-10 -top-12 w-56 h-56 opacity-[0.06]" aria-hidden="true">
      <svg viewBox="0 0 200 200" className="w-full h-full" fill="none">
        <g stroke="#E5CC8F"><circle cx="100" cy="100" r="92" strokeWidth="0.6" /><circle cx="100" cy="100" r="60" strokeWidth="0.5" /></g>
        {[0, 90, 180, 270].map((deg) => (
          <g key={deg} transform={`rotate(${deg} 100 100)`}>
            <path d="M100,16 L92,90 L100,100 Z" fill="#F5EDD7" />
            <path d="M100,16 L108,90 L100,100 Z" fill="#C9A55C" />
          </g>
        ))}
      </svg>
    </div>
  )
}

/* ──────────────────────────────── quick nav ──────────────────────────── */

function QuickNav({ posts }: { posts: NewsPost[] }) {
  return (
    <nav
      aria-label="Sommaire des nouveautés"
      className="mt-6 rounded-xl border border-sand-500/15 bg-ocean-900/60 px-3 py-2.5"
    >
      <ul className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
        {posts.map((p) => (
          <li key={p.slug} className="min-w-0 sm:shrink-0">
            <a
              href={`#${p.slug}`}
              className="flex sm:inline-flex items-center gap-1.5 rounded-full border border-sand-500/20 bg-ocean-900/40 px-3 py-1.5 text-xs text-ocean-100 hover:border-sand-300/50 hover:text-sand-200 transition-colors"
            >
              <LanternIcon className="w-3.5 h-3.5 text-sand-300 shrink-0" />
              <span className="truncate">{p.title}</span>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}

/* ──────────────────────────────── post ───────────────────────────────── */

function Post({ post }: { post: NewsPost }) {
  return (
    <article
      id={post.slug}
      className="scroll-mt-32 rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/25 to-[#06222b]/40 p-6 sm:p-8"
    >
      <header className="border-b border-sand-500/12 pb-5">
        <p className="text-[11px] uppercase tracking-[0.28em] text-ocean-300 flex items-center gap-2">
          <LanternIcon className="w-3.5 h-3.5 text-sand-300" />
          <time dateTime={post.publishedAt}>{frenchDate(post.publishedAt)}</time>
        </p>
        <h2 className="mt-2 font-display text-2xl sm:text-3xl text-sand-300 leading-tight">{post.title}</h2>
        {post.teaser && <p className="mt-3 text-ocean-100 leading-relaxed max-w-3xl">{post.teaser}</p>}
      </header>

      <div className="mt-6 space-y-5">
        {post.blocks.map((block, i) => (
          <Block key={i} block={block} />
        ))}
      </div>
    </article>
  )
}

/** Rend un bloc du contrat. Texte brut uniquement — jamais de HTML injecté. */
function Block({ block }: { block: NewsBlock }) {
  switch (block.type) {
    case 'h':
      return (
        <h3 className="flex items-center gap-2 font-display text-lg sm:text-xl text-sand-200 pt-1">
          <ChevronRight className="w-4 h-4 shrink-0 text-sand-500" />
          {block.text}
        </h3>
      )
    case 'ul':
      return (
        <ul className="space-y-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-ocean-100 leading-relaxed">
              <span aria-hidden="true" className="mt-[7px] w-1.5 h-1.5 shrink-0 rotate-45 rounded-[1px] bg-sand-500" />
              <span className="min-w-0">{item}</span>
            </li>
          ))}
        </ul>
      )
    case 'stats':
      return (
        <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
          {block.items.map((s, i) => (
            <div
              key={i}
              className="rounded-xl border border-sand-500/20 bg-ocean-900/40 px-3 py-2.5 text-center"
            >
              <dd className="font-display text-xl sm:text-2xl text-sand-300 leading-none tabular-nums">{s.value}</dd>
              <dt className="mt-1.5 text-[10px] uppercase tracking-[0.14em] text-ocean-300 leading-snug">{s.label}</dt>
            </div>
          ))}
        </dl>
      )
    case 'p':
    default:
      return <p className="text-ocean-100 leading-relaxed">{block.text}</p>
  }
}

/* ──────────────────────────────── empty ──────────────────────────────── */

function EmptyState() {
  return (
    <div className="mt-10 rounded-2xl border border-sand-500/15 bg-ocean-900/30 p-8 text-center">
      <PennantIcon className="w-8 h-8 mx-auto text-sand-300" />
      <p className="font-display text-xl text-sand-300 mt-2">Rien à signaler pour le moment</p>
      <p className="text-sm text-ocean-100 mt-1.5 max-w-xl mx-auto">
        La vigie n'a rien vu passer. Les prochaines annonces de l'archipel seront affichées ici.
      </p>
    </div>
  )
}
